import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
  Alert,
  Button,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Typography,
  Radio,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';

import DisplayValue from 'components/DisplayValue/DisplayValue';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import useAgent from 'hooks/useAgent';
import useCeaseAndDesistStatus from 'hooks/useCeaseAndDesistStatus';
import useModalControls from 'hooks/useModalControls';
import {
  UPDATE_CUSTOMER_PREFERENCES,
  REMOVE_CEASE_AND_DESIST,
} from 'mutations/customerMeta';
import { UPDATE_STATEMENT_PREFERENCES } from 'mutations/preferences';

import { formatDate, formatDateToSubmit } from 'utilities/datesAndTimes';
import useCustomerInfo from 'hooks/useCustomerInfo';
import useSubmitProgress from 'hooks/useSubmitProgress';

const { Text, Title } = Typography;
const { Item } = Form;

const LOADING_SKELETON = {
  title: false,
  paragraph: { rows: 2, width: ['120px', '140px'] },
};

const CeaseAndDesist = () => {
  const agent = useAgent();
  const editCeaseAndDesistModal = useModalControls();
  const { ceaseAndDesist, ceaseAndDesistEnabled, ceaseAndDesistQuery } =
    useCeaseAndDesistStatus();

  const loading = !!ceaseAndDesistQuery?.loading;

  return (
    <div>
      <EditCeaseAndDesistModal
        ceaseAndDesistEnabled={ceaseAndDesistEnabled}
        {...editCeaseAndDesistModal}
      />
      <Flex justify="space-between">
        <Title level={5}> Cease and Desist</Title>
        {agent.isSuperUser && (
          <Button
            data-testid="change-button"
            type="link"
            icon={<EditOutlined style={{ fontSize: '1.5rem' }} />}
            onClick={editCeaseAndDesistModal.show}
            disabled={loading}
            size="small"
          />
        )}
      </Flex>
      <Divider style={{ margin: '0 0 var(--spacing-sm)' }} />
      <QueryBoundary query={ceaseAndDesistQuery} skeleton={LOADING_SKELETON}>
        {ceaseAndDesistEnabled && (
          <Alert
            banner
            type="info"
            title={`Requested by ${
              ceaseAndDesist?.communicationFrom
            } on ${formatDate(ceaseAndDesist?.notificationDate)}`}
            style={{ marginBottom: 'var(--spacing-sm)' }}
          />
        )}
        <Row gutter={['var(--spacing)', 'var(--spacing-lg)']}>
          <Col span={24}>
            <DisplayValue label="Current Status:">
              <span data-testid="cease-desist-span">
                {ceaseAndDesistEnabled ? 'ON' : 'OFF'}
              </span>
            </DisplayValue>
          </Col>
        </Row>
      </QueryBoundary>
    </div>
  );
};

const EditCeaseAndDesistModal = ({
  ceaseAndDesistEnabled,
  open,
  hide,
  ...modalProps
}) => {
  const [form] = Form.useForm();
  const { customerId } = useParams();
  const { customerInfo } = useCustomerInfo();
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [failureMessage, setFailureMessage] = useState('');
  const accountIds = customerInfo.accountIds;

  const [ceaseAndDesistActive, setCeaseAndDesistActive] = useState(false);

  const REQUESTER_TYPES = {
    CARDHOLDER: customerInfo?.fullName || 'Cardholder',
    BANKRUPTCY: 'Bankruptcy',
    ATTORNEY: 'Attorney',
    THIRD_PARTY: '3rd party',
    BANK_POLICY: 'Bank Policy',
    OTHER: 'Other',
  };

  const [addCeaseAndDesistStatus, addCeaseAndDesistMutation] = useMutation(
    UPDATE_CUSTOMER_PREFERENCES,
    {
      refetchQueries: ['GetCustomerCommPreferences'],
    },
  );

  const [removeCeaseAndDesistStatus, removeCeaseAndDesistMutation] =
    useMutation(REMOVE_CEASE_AND_DESIST, {
      refetchQueries: ['GetCustomerCommPreferences'],
    });

  const [updateStatementPreference, updateStatementPreferencesMutation] =
    useMutation(UPDATE_STATEMENT_PREFERENCES, {
      refetchQueries: ['GetAccountStatementPreferences'],
    });

  const executeCeaseAndDesist = async ({
    markInitiated,
    markCompleted,
    reset,
  }) => {
    setFailureMessage('');
    markInitiated();

    const cleanup = () => {
      markCompleted();
      setTimeout(() => {
        hide();
        reset();
      }, 2000);
    };

    if (ceaseAndDesistActive) {
      await removeCeaseAndDesistStatus({ variables: { customerId } });
      cleanup();
    } else {
      try {
        await form.validateFields();

        const selectedRadio = form.getFieldValue('requester-radio');
        let communicationFrom = selectedRadio;

        if (selectedRadio === REQUESTER_TYPES.OTHER) {
          communicationFrom = form.getFieldValue('requester-input');
        }

        const floridaIndicator =
          customerInfo?.customer?.contactInfo?.address?.state === 'FL';

        if (!addCeaseAndDesistMutation.data) {
          await addCeaseAndDesistStatus({
            variables: {
              customerId,
              communicationRequest: {
                ceaseDesist: {
                  communicationFrom,
                  floridaIndicator,
                  notificationDate: formatDateToSubmit(Date.now()),
                },
              },
            },
          });
        }

        const preferenceRequests = accountIds.map((accountId) => {
          return updateStatementPreference({
            variables: {
              accountId,
              customerId,
              statementPreferencesUpdateInput: {
                isPaperlessStatement: false,
              },
            },
          });
        });

        await Promise.all(preferenceRequests);

        cleanup();
      } catch (error) {}
    }
  };

  const submitProgressConfig = {
    successMessage: `Cease and desist enabled.`,
    successTestId: 'cease-and-desist-success-message',
    onSubmit: executeCeaseAndDesist,
  };

  if (ceaseAndDesistActive) {
    submitProgressConfig.steps = [
      {
        queries: [removeCeaseAndDesistMutation],
        text: 'Remove Cease and Desist',
        errorMessage: `Failed to remove cease and desist. Please retry.`,
        errorTestId: 'remove-cease-and-desist-failure-alert',
      },
    ];
  } else {
    submitProgressConfig.steps = [
      {
        queries: [addCeaseAndDesistMutation],
        text: 'Applying cease and desist status',
        errorMessage: `Failed to apply cease and desist. Please retry.`,
        errorTestId: 'apply-cease-and-desist-failure-alert',
      },
    ];

    if (accountIds?.length) {
      submitProgressConfig.steps.push({
        queries: [updateStatementPreferencesMutation],
        text: 'Setting paper preference across accounts',
        errorMessage: `Failed to set paper preference across all accounts. Please update account statement settings manually`,
        errorTestId: 'set-paper-preference-failure-alert',
      });
    }
  }

  const { handleSubmit, renderProgress, submissionInitiated } =
    useSubmitProgress(submitProgressConfig);

  const renderForm = () => {
    return ceaseAndDesistActive ? (
      <Text>Turn Cease & Desist status OFF</Text>
    ) : (
      <Form form={form}>
        <Text>Who is requesting this change?</Text>
        <Item
          name="requester-radio"
          style={{ marginTop: '.5rem' }}
          rules={[
            {
              required: true,
              message: 'Requester is required',
            },
          ]}
        >
          <Radio.Group
            onChange={(e) =>
              setShowOtherInput(e.target.value === REQUESTER_TYPES.OTHER)
            }
          >
            <Space orientation="vertical">
              {Object.keys(REQUESTER_TYPES).map((key) => {
                return (
                  <Radio
                    value={REQUESTER_TYPES[key]}
                    key={key}
                    data-testid={`radio-${REQUESTER_TYPES[key]}`.toLowerCase()}
                  >
                    {REQUESTER_TYPES[key]}
                  </Radio>
                );
              })}
            </Space>
          </Radio.Group>
        </Item>
        {showOtherInput && (
          <Item
            name="requester-input"
            label="Name"
            style={{ marginTop: '.5rem' }}
            rules={[
              {
                required: true,
                message: 'Name is required',
              },
            ]}
          >
            <Input />
          </Item>
        )}
        {failureMessage ? <Alert type="error" title={failureMessage} /> : null}
      </Form>
    );
  };

  useEffect(() => {
    if (open) {
      setCeaseAndDesistActive(ceaseAndDesistEnabled);
    }
  }, [open]);

  return (
    <Modal
      {...modalProps}
      open={open}
      title="Change Cease & Desist status"
      onOk={handleSubmit}
      okText="Confirm"
      cancelText="Close"
      confirmLoading={
        addCeaseAndDesistMutation.loading ||
        removeCeaseAndDesistMutation.loading
      }
    >
      {submissionInitiated ? renderProgress() : renderForm()}
    </Modal>
  );
};

export default CeaseAndDesist;
